import {useState, useEffect} from 'react';

import {type FallowingInitialState} from 'interfaces';
import {useInputMenuContext} from 'context/InputMenu';

import addEditFallowingBackendHandler, {handleCommunicationSuccess} from 'helpers/addEdit/fallowing';
import useParseURL from 'hooks/useParseURL';
import useIsMounted from 'hooks/useIsMounted';
import {addDays, dateReader} from 'helpers/UnitsFormatHandler';
import {useGetFallowing} from 'hooks/swr';
import useGetPlanTreeNavigation from 'hooks/plan/useGetPlanTreeNavigation';
import {useTranslation} from 'react-i18next';
import useGetLongTermRange from 'hooks/useGetLongTermRange';

const SIXTY_DAYS = 60;

function useAddEditSiteFallowing() {
  const {planId} = useParseURL();
  const isMounted = useIsMounted();

  const inputMenuContext = useInputMenuContext();
  const inputMenuState = inputMenuContext?.state;
  const inputMenuDispatch = inputMenuContext?.dispatch;

  const {t} = useTranslation('common');

  const formData = inputMenuState?.formData;
  const fallowingId = formData?.id;
  const siteId = formData?.siteId;
  const date = formData?.date;

  const {data: fallowing} = useGetFallowing({resourceId: fallowingId});
  const {planTreeNavigation} = useGetPlanTreeNavigation({planId, isMounted});
  const {startDate, endDate} = useGetLongTermRange();

  const initialState: FallowingInitialState | undefined =
    !fallowingId && date && siteId
      ? {
          siteId,
          startDate: date,
          endDate: addDays(date, SIXTY_DAYS),
        }
      : undefined;

  const [siteFallowing, setSiteFallowing] = useState<FallowingInitialState | undefined>(initialState);
  const [createUpdateSiteFallowing, setCreateUpdateSiteFallowing] = useState<boolean>(false);
  const method = inputMenuState?.method;

  const communicationSuccess = () => {
    method &&
      planTreeNavigation &&
      siteId &&
      handleCommunicationSuccess({
        setCreateUpdateSiteFallowing,
        setSiteFallowing,
        planId,
        siteId,
        treePlan: planTreeNavigation,
      });
  };

  useEffect(() => {
    fallowing &&
      setSiteFallowing({
        ...fallowing,
        startDate: dateReader(fallowing.startDate),
        endDate: dateReader(fallowing.endDate),
      });
  }, [fallowing]);

  useEffect(() => {
    method &&
      siteFallowing &&
      createUpdateSiteFallowing &&
      addEditFallowingBackendHandler({
        siteFallowing,
        method,
        createUpdateSiteFallowing,
        setCreateUpdateSiteFallowing,
        inputMenuDispatch,
        fallowingId,
        communicationSuccess,
        isMounted,
        t,
      });
  }, [createUpdateSiteFallowing, siteFallowing]);

  return {
    siteFallowing,
    setSiteFallowing,
    setCreateUpdateSiteFallowing,
    inputMenuContext,
  };
}

export default useAddEditSiteFallowing;
